import {
  useListExpenseAccounts,
  useListProperties,
  useListResidents,
  useListUnits,
  useListVendors,
  useListWorkOrderAssignees,
} from '@myrentalmate/api-client';
import { useMemo } from 'react';

import { okData, okItems } from '../../lib/apiData';

const PAGE = { page_size: 200 };

/** "Where" as one picker, so a unit can't be paired with the wrong property. */
export function useWherePicker() {
  const properties = okItems(useListProperties(PAGE).data);
  const units = okItems(useListUnits(PAGE).data);
  return useMemo(() => {
    const options = properties.flatMap((p) => [
      { value: `p:${p.id}`, label: `${p.name} (whole property)` },
      ...units
        .filter((u) => u.property_id === p.id)
        .map((u) => ({ value: `u:${u.id}`, label: `${p.name} · ${u.unit_number}` })),
    ]);
    const propertyOfUnit = new Map(units.map((u) => [u.id, u.property_id]));
    function parse(value: string): { property_id: number; unit_id: number | null } {
      const [kind, id] = value.split(':');
      return kind === 'u'
        ? { property_id: propertyOfUnit.get(Number(id)) ?? 0, unit_id: Number(id) }
        : { property_id: Number(id), unit_id: null };
    }
    return { options, parse };
  }, [properties, units]);
}

export const whereValue = (row: { property_id: number; unit_id: number | null }) =>
  row.unit_id === null ? `p:${row.property_id}` : `u:${row.unit_id}`;

/** Staff and vendors in one list. A work order has one or the other. */
export function useAssigneePicker() {
  const staffData = useListWorkOrderAssignees().data;
  const vendorData = useListVendors(PAGE).data;
  return useMemo(() => {
    const staff = okData(staffData)?.items ?? [];
    const vendors = okItems(vendorData);
    return [
      { value: '', label: 'Nobody yet' },
      ...staff.map((s) => ({ value: `user:${s.user_id}`, label: `${s.name} (staff)` })),
      ...vendors.map((v) => ({
        value: `vendor:${v.id}`,
        label: `${v.name}${v.trade ? ` (${v.trade})` : ' (vendor)'}`,
      })),
    ];
  }, [staffData, vendorData]);
}

export function parseAssignee(value: string): {
  assigned_user_id: number | null;
  vendor_id: number | null;
} {
  const [kind, id] = value.split(':');
  return {
    assigned_user_id: kind === 'user' ? Number(id) : null,
    vendor_id: kind === 'vendor' ? Number(id) : null,
  };
}

export const assigneeValue = (row: {
  assigned_user_id: number | null;
  vendor_id: number | null;
}) =>
  row.vendor_id !== null
    ? `vendor:${row.vendor_id}`
    : row.assigned_user_id !== null
      ? `user:${row.assigned_user_id}`
      : '';

export function useResidentPicker() {
  const residents = okItems(useListResidents(PAGE).data);
  return useMemo(
    () => [
      { value: '', label: 'No resident' },
      ...residents.map((r) => ({ value: String(r.id), label: `${r.last_name}, ${r.first_name}` })),
    ],
    [residents],
  );
}

export function useVendorOptions() {
  const vendors = okItems(useListVendors(PAGE).data);
  return useMemo(() => vendors.map((v) => ({ value: String(v.id), label: v.name })), [vendors]);
}

export function useExpenseOptions(enabled = true) {
  const data = useListExpenseAccounts({ query: { enabled } }).data;
  return useMemo(
    () => (okData(data)?.items ?? []).map((a) => ({ value: a.code, label: a.name })),
    [data],
  );
}
